import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Export data to Excel format
 */
export const exportToExcel = (data, filename = 'export') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Generate buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

/**
 * Export data to PDF format
 */
export const exportToPDF = (data, filename = 'export', options = {}) => {
  const { title = 'Report', columns = [], language = 'es' } = options;
  
  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(18);
  doc.setTextColor(85, 153, 117); // Primary color
  doc.text(title, 14, 22);
  
  // Add date
  doc.setFontSize(10);
  doc.setTextColor(100);
  const dateText = language === 'es' ? 'Generado el' : 'Generated on';
  doc.text(`${dateText}: ${new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}`, 14, 30);
  
  // If columns provided, use autotable
  if (columns.length > 0 && Array.isArray(data)) {
    const tableColumns = columns.map(col => ({
      header: col.header,
      dataKey: col.key
    }));
    
    doc.autoTable({
      columns: tableColumns,
      body: data,
      startY: 40,
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [85, 153, 117],
        textColor: 255
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250]
      }
    });
  } else {
    // Simple text export
    doc.setFontSize(10);
    doc.setTextColor(0);
    let y = 40;
    
    if (typeof data === 'object') {
      Object.entries(data).forEach(([key, value]) => {
        if (y > 280) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${key}: ${JSON.stringify(value)}`, 14, y);
        y += 7;
      });
    }
  }
  
  doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data, filename = 'export') => {
  if (!Array.isArray(data) || data.length === 0) {
    console.error('No data to export');
    return;
  }
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values with commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
};

/**
 * Export analytics report
 */
export const exportAnalyticsReport = (analytics, format = 'pdf', language = 'es') => {
  const labels = {
    es: {
      title: 'Reporte de Analytics',
      financial: 'Métricas Financieras',
      funnel: 'Embudo de Ventas',
      campaigns: 'Rendimiento de Campañas',
      revenue: 'Ingresos',
      profit: 'Ganancia',
      margin: 'Margen',
      customers: 'Clientes',
      conversion: 'Conversión'
    },
    en: {
      title: 'Analytics Report',
      financial: 'Financial Metrics',
      funnel: 'Sales Funnel',
      campaigns: 'Campaign Performance',
      revenue: 'Revenue',
      profit: 'Profit',
      margin: 'Margin',
      customers: 'Customers',
      conversion: 'Conversion'
    }
  };
  
  const t = labels[language] || labels.es;
  
  if (format === 'excel') {
    // Multiple sheets for Excel
    const workbook = XLSX.utils.book_new();
    
    // Financial sheet
    if (analytics.financial) {
      const financialData = [
        { metric: t.revenue, value: `$${analytics.financial.total_revenue?.toLocaleString()}` },
        { metric: t.profit, value: `$${analytics.financial.gross_profit?.toLocaleString()}` },
        { metric: t.margin, value: `${analytics.financial.profit_margin}%` },
      ];
      const ws1 = XLSX.utils.json_to_sheet(financialData);
      XLSX.utils.book_append_sheet(workbook, ws1, t.financial);
    }
    
    // Funnel sheet
    if (analytics.funnel) {
      const funnelData = [
        { stage: 'Leads', count: analytics.funnel.total_leads },
        { stage: 'Prospects', count: analytics.funnel.total_prospects },
        { stage: 'Qualified', count: analytics.funnel.total_qualified },
        { stage: 'Negotiation', count: analytics.funnel.total_negotiation },
        { stage: 'Won', count: analytics.funnel.total_won },
        { stage: 'Lost', count: analytics.funnel.total_lost },
        { stage: 'Inquiries', count: analytics.funnel.total_inquiries },
      ];
      const ws2 = XLSX.utils.json_to_sheet(funnelData);
      XLSX.utils.book_append_sheet(workbook, ws2, t.funnel);
    }
    
    // Campaigns sheet
    if (analytics.campaign_performance) {
      const ws3 = XLSX.utils.json_to_sheet(analytics.campaign_performance);
      XLSX.utils.book_append_sheet(workbook, ws3, t.campaigns);
    }
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `analytics_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    
  } else if (format === 'pdf') {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(85, 153, 117);
    doc.text(t.title, 14, 22);
    
    // Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`${new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}`, 14, 30);
    
    let y = 45;
    
    // Financial section
    if (analytics.financial) {
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(t.financial, 14, y);
      y += 10;
      
      doc.autoTable({
        startY: y,
        head: [['Métrica', 'Valor']],
        body: [
          [t.revenue, `$${analytics.financial.total_revenue?.toLocaleString()}`],
          [t.profit, `$${analytics.financial.gross_profit?.toLocaleString()}`],
          [t.margin, `${analytics.financial.profit_margin}%`],
        ],
        headStyles: { fillColor: [85, 153, 117] }
      });
      y = doc.lastAutoTable.finalY + 15;
    }
    
    // Funnel section
    if (analytics.funnel) {
      doc.setFontSize(14);
      doc.text(t.funnel, 14, y);
      y += 10;
      
      doc.autoTable({
        startY: y,
        head: [['Etapa', 'Cantidad', 'Tasa']],
        body: [
          ['Leads', analytics.funnel.total_leads, '-'],
          ['Prospects', analytics.funnel.total_prospects, `${analytics.funnel.lead_to_prospect_rate}%`],
          ['Qualified', analytics.funnel.total_qualified, `${analytics.funnel.prospect_to_qualified_rate}%`],
          ['Negotiation', analytics.funnel.total_negotiation, `${analytics.funnel.qualified_to_negotiation_rate}%`],
          ['Won', analytics.funnel.total_won, `${analytics.funnel.negotiation_to_won_rate}%`],
          ['Inquiries', analytics.funnel.total_inquiries, 'N/A'],
        ],
        headStyles: { fillColor: [85, 153, 117] }
      });
    }
    
    doc.save(`analytics_report_${new Date().toISOString().split('T')[0]}.pdf`);
  }
};

/**
 * Export customers data
 */
export const exportCustomers = (customers, format = 'excel', language = 'es') => {
  const columns = [
    { key: 'name', header: language === 'es' ? 'Nombre' : 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: language === 'es' ? 'Teléfono' : 'Phone' },
    { key: 'status', header: language === 'es' ? 'Estado' : 'Status' },
    { key: 'created_at', header: language === 'es' ? 'Fecha de registro' : 'Created' },
  ];
  
  const data = customers.map(c => ({
    name: c.name,
    email: c.email || '',
    phone: c.phone || '',
    status: c.status,
    created_at: c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
  }));
  
  if (format === 'excel') {
    exportToExcel(data, 'customers');
  } else if (format === 'pdf') {
    exportToPDF(data, 'customers', { 
      title: language === 'es' ? 'Listado de Clientes' : 'Customer List',
      columns,
      language 
    });
  } else if (format === 'csv') {
    exportToCSV(data, 'customers');
  }
};
